import RefreshIcon from "@mui/icons-material/Refresh";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { skipToken } from "@reduxjs/toolkit/query";
import { HttpStatusCode } from "axios";
import { getApiErrorData, getApiErrorStatus, getErrorMessageFromUnknown } from "_shared/_errors/api-error-message";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import Loader from "_shared/_views/loading/loader";
import { StatusChip, type StatusTone } from "_shared/_views/tables/status-chip";
import { formatDate } from "_shared/formatting/dates";
import { useGetDocumentsByIdsQuery } from "documents/_api/document-api";
import Document from "documents/document";
import { useState } from "react";
import { Link as RouterLink, useLocation, useParams } from "react-router";
import {
  policyReplacementsApi,
  useGetPolicyReplacementQuery,
  useRetryPolicyReplacementTargetMutation,
  useWithdrawPolicyReplacementMutation,
  useWithdrawPolicyReplacementTargetMutation,
} from "work/policy-replacements/_api/policy-replacement-api";
import type { PolicyReplacementTargetJSON } from "work/policy-replacements/_schemas/policy-replacement-schema";
import PolicyReplacementCreatedOutcome from "work/policy-replacements/_views/policy-replacement-created-outcome";
import type { PolicyReplacementCreatedState } from "work/policy-replacements/_views/replace-policy-page";
import PolicyReplacement, {
  POLICY_REPLACEMENT_PROGRESS_DESCRIPTIONS,
} from "work/policy-replacements/policy-replacement";
import { revisionPath, workAgreementPath } from "work/policy-replacements/policy-replacement-routes";
import { useGetWorkAgreementsByIdsQuery } from "work/work-agreements/_api/work-agreement-api";

/**
 * How often the page asks again while a target can still move on its own. The worker passes every
 * minute in production and a vendor answers in its own time, so this is a courtesy to a reader who
 * keeps the page open, not a promise of live updates.
 */
export const PROGRESS_POLL_MILLISECONDS = 15_000;

const PageContainer = styled(Paper)(({ theme }) => ({
  boxSizing: "border-box",
  margin: theme.spacing(1),
  maxHeight: `calc(100% - ${theme.spacing(2)})`,
  overflow: "auto",
  padding: theme.spacing(2),
}));
const Summary = styled("div")(({ theme }) => ({
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(2),
  maxWidth: "56rem",
}));
const Counts = styled("ul")(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(1),
  listStyle: "none",
  margin: theme.spacing(0, 0, 2),
  padding: 0,
}));
const Toolbar = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(1),
  justifyContent: "space-between",
  marginBottom: theme.spacing(1),
}));
const RowActions = styled("div")(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(1),
}));

type TargetAction = "withdraw" | "retry";

/** What a refused or failed withdraw or retry says. A 409 carries the reason the state forbids it. */
export function describeActionFailure(error: unknown, action: TargetAction | "withdraw-all"): string {
  const status = getApiErrorStatus(error);
  if (status === HttpStatusCode.Conflict) {
    const data = getApiErrorData(error);
    const detail = typeof data === "object" && data !== null && "detail" in data ? (data as { detail: unknown }).detail : undefined;
    if (typeof detail === "string" && detail.trim()) return detail;
    return "This agreement's part of the replacement has moved on. The page has been refreshed.";
  }
  if (status === HttpStatusCode.Forbidden) return "You may not change this replacement.";
  if (status === HttpStatusCode.NotFound) return "This replacement no longer exists.";
  const fallback = action === "retry" ? "The retry did not go through." : "The withdrawal did not go through.";
  return getErrorMessageFromUnknown(error) ?? fallback;
}

/** Runs one withdraw or retry and reports a failure. At module scope so the component stays compiled. */
async function runTargetAction(run: () => Promise<unknown>, action: TargetAction | "withdraw-all"): Promise<void> {
  try {
    await run();
  } catch (error) {
    enqueueSnackbar(describeActionFailure(error, action), { variant: "error" });
  }
}

/**
 * Where one replacement stands: each agreement it reaches that the reader may see, its state, the
 * reason where the worker stopped, the revision it opened, and what the client may still do about it
 * (#6257). The replacement has no state of its own, so the page leads with a count per state.
 *
 * Notifications link here (#6256), so the address is `/policy-replacements/{id}`.
 */
export default function PolicyReplacementProgressPage() {
  const { policyReplacementId } = useParams();
  const location = useLocation();
  const confirm = useConfirmDialog();
  const createdState = (location.state ?? null) as PolicyReplacementCreatedState | null;
  const [busyTarget, setBusyTarget] = useState<string | null>(null);
  const [isWithdrawingAll, setIsWithdrawingAll] = useState(false);

  const replacementArgs = policyReplacementId ? { policyReplacementId } : skipToken;
  // The answer already held decides whether to keep asking: once nothing can move without the
  // client, polling stops. Somebody else can still move it - a retry or a withdrawal from another
  // browser - so the page reads afresh whenever it is opened or the window comes back into focus, and
  // the reader can ask again with Refresh, rather than trusting an answer cached from an earlier visit.
  const held = policyReplacementsApi.endpoints.getPolicyReplacement.useQueryState(replacementArgs);
  const keepAsking = !held.data || PolicyReplacement.fromJSON(held.data)?.isStillMoving === true;
  const replacementQuery = useGetPolicyReplacementQuery(replacementArgs, {
    pollingInterval: keepAsking ? PROGRESS_POLL_MILLISECONDS : 0,
    skipPollingIfUnfocused: true,
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
  });
  const replacement = PolicyReplacement.fromJSON(replacementQuery.data);

  const workAgreementIds = replacement ? [...new Set(replacement.targets.map((target) => target.workAgreementId))] : [];
  const agreementsQuery = useGetWorkAgreementsByIdsQuery(
    workAgreementIds.length > 0 ? { workAgreementIds } : skipToken,
  );
  const documentsQuery = useGetDocumentsByIdsQuery(
    replacement ? { documentIds: [replacement.replacedDocumentId, replacement.replacementDocumentId] } : skipToken,
  );
  const [withdrawAll] = useWithdrawPolicyReplacementMutation();
  const [withdrawTarget] = useWithdrawPolicyReplacementTargetMutation();
  const [retryTarget] = useRetryPolicyReplacementTargetMutation();

  const agreementName = (workAgreementId: string) =>
    agreementsQuery.data?.found.find((agreement) => agreement.id === workAgreementId)?.name ??
    createdState?.agreementNames[workAgreementId] ??
    "An agreement";
  const documentName = (documentId: string | undefined) => {
    const found = documentsQuery.data?.find((document) => document.id.value === documentId);
    return found instanceof Document ? found.name.value : "a client policy";
  };

  async function handleWithdrawAll(policyReplacementId: string) {
    const answer = await confirm({
      title: "Withdraw the replacement?",
      message:
        "Every agreement whose revision is not yet hired keeps the old policy, and each open revision is cancelled. " +
        "Agreements that already adopted the new policy keep it.",
      okButtonText: "Withdraw all",
    });
    if (answer === ConfirmResponse.Cancel) return;
    setIsWithdrawingAll(true);
    await runTargetAction(() => withdrawAll({ policyReplacementId }).unwrap(), "withdraw-all");
    setIsWithdrawingAll(false);
  }

  async function handleTargetAction(policyReplacementId: string, target: PolicyReplacementTargetJSON, action: TargetAction) {
    if (action === "withdraw") {
      const answer = await confirm({
        title: "Withdraw this agreement?",
        message: `${agreementName(target.workAgreementId)} keeps the old policy. An open revision is cancelled, and its vendor is told.`,
        okButtonText: "Withdraw",
      });
      if (answer === ConfirmResponse.Cancel) return;
    }
    const args = { policyReplacementId, workAgreementId: target.workAgreementId };
    setBusyTarget(target.targetId);
    await runTargetAction(
      () => (action === "withdraw" ? withdrawTarget(args).unwrap() : retryTarget(args).unwrap()),
      action,
    );
    setBusyTarget(null);
  }

  if (!policyReplacementId) return <PageContainer>No replacement was named.</PageContainer>;
  if (replacementQuery.isLoading) return <Loader text="Loading the replacement..." />;
  if (replacementQuery.isError || !replacement) {
    const status = getApiErrorStatus(replacementQuery.error);
    return (
      <PageContainer>
        <Alert severity="error" role="alert">
          {status === HttpStatusCode.Forbidden
            ? "You may not see this policy replacement."
            : status === HttpStatusCode.NotFound
              ? "This policy replacement does not exist."
              : (getErrorMessageFromUnknown(replacementQuery.error) ?? "The policy replacement could not be loaded.")}
        </Alert>
      </PageContainer>
    );
  }

  const isBusy = isWithdrawingAll || busyTarget !== null;

  return (
    <PageContainer>
      <Typography variant="h5" component="h1">
        Policy replacement
      </Typography>
      <Summary>
        <Typography variant="body1">
          Replacing <b>{documentName(replacement.replacedDocumentId)}</b> with{" "}
          <b>{documentName(replacement.replacementDocumentId)}</b>, asked for on{" "}
          {formatDate(replacement.createdDate) ?? "an unknown date"}.
        </Typography>
        <Typography variant="body2" data-testid="policy-replacement-reason">
          Reason given to the vendors: {replacement.reason}
        </Typography>
      </Summary>
      {createdState && (
        <PolicyReplacementCreatedOutcome created={createdState.created} agreementNames={createdState.agreementNames} />
      )}
      <Toolbar>
        <Counts aria-label="Agreements in each state">
          {replacement.counts.map(({ progress, count }) => (
            <li key={progress}>
              <Chip
                color={chipColorOf(POLICY_REPLACEMENT_PROGRESS_DESCRIPTIONS[progress].tone)}
                variant="outlined"
                label={`${POLICY_REPLACEMENT_PROGRESS_DESCRIPTIONS[progress].label}: ${count}`}
              />
            </li>
          ))}
        </Counts>
        <RowActions>
          <Button
            variant="text"
            startIcon={<RefreshIcon />}
            loading={replacementQuery.isFetching}
            disabled={isBusy}
            onClick={() => void replacementQuery.refetch()}
          >
            Refresh
          </Button>
          {replacement.canWithdrawAny && (
            <Button variant="outlined" color="error" loading={isWithdrawingAll} disabled={isBusy} onClick={() => handleWithdrawAll(replacement.id)}>
              Withdraw all
            </Button>
          )}
        </RowActions>
      </Toolbar>
      <TableContainer>
        <Table size="small" aria-label="Agreements in this replacement">
          <TableHead>
            <TableRow>
              <TableCell>Agreement</TableCell>
              <TableCell>State</TableCell>
              <TableCell>Reason</TableCell>
              <TableCell>Revision</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {replacement.targets.map((target) => {
              const description = POLICY_REPLACEMENT_PROGRESS_DESCRIPTIONS[target.progress];
              const name = agreementName(target.workAgreementId);
              return (
                <TableRow key={target.targetId} data-testid="policy-replacement-target">
                  <TableCell>
                    <Link component={RouterLink} to={workAgreementPath(target.workAgreementId)}>
                      {name}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={description.meaning}>
                      <span>
                        <StatusChip label={description.label} tone={description.tone} />
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{target.reason}</TableCell>
                  <TableCell>
                    {target.revisionProposalId && (
                      <Link component={RouterLink} to={revisionPath(target.revisionProposalId)}>
                        Open the revision
                      </Link>
                    )}
                  </TableCell>
                  <TableCell>
                    <RowActions>
                      {PolicyReplacement.canRetry(target) && (
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={isBusy}
                          loading={busyTarget === target.targetId}
                          aria-label={`Retry ${name}`}
                          onClick={() => handleTargetAction(replacement.id, target, "retry")}
                        >
                          Retry
                        </Button>
                      )}
                      {PolicyReplacement.canWithdraw(target) && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          disabled={isBusy}
                          aria-label={`Withdraw ${name}`}
                          onClick={() => handleTargetAction(replacement.id, target, "withdraw")}
                        >
                          Withdraw
                        </Button>
                      )}
                    </RowActions>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {replacement.targets.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          None of the agreements in this replacement are ones you may see.
        </Typography>
      )}
    </PageContainer>
  );
}

/** A count chip takes its state's tone; MUI calls the neutral one "default". */
function chipColorOf(tone: StatusTone): "default" | Exclude<StatusTone, "neutral"> {
  return tone === "neutral" ? "default" : tone;
}
